/* eslint-disable @typescript-eslint/no-explicit-any */
import axios, {AxiosError, ResponseType} from 'axios';
import Cookies from 'js-cookie';
import queryString from 'query-string';
import {Locale} from 'types';
import {toSnakeCase, withValues} from 'utils/object';
import ApiError from './classes/error/api-error';
import ApiSuccess from './classes/success/api-success';
import {cleanFormData, encodeFormData} from './utils';
import ResponseValidator, {ErrorValidator} from './validator';

export enum Accept {
    JSON = 'application/json',
    TEXT_HTML = 'text/html,application/xhtml+xml,application/xml;q=0.9,image/avif,image/webp,image/apng,*/*;q=0.8,application/signed-exchange;v=b3;q=0.7',
}

export enum ContentType {
    FORM_URLENCODED = 'application/x-www-form-urlencoded',
    JSON = 'application/json',
}

type Request = {
    accept?: Accept;
    arrayFormat?: 'comma' | 'bracket';
    contentType?: ContentType;
    formData?: Record<string, unknown> | FormData;
    locale?: Locale;
    method?: 'GET' | 'POST' | 'PATCH' | 'PUT' | 'DELETE';
    params?: Record<string, unknown>;
    responseType?: ResponseType;
    uri: string;
};

export const request = ({
    accept = Accept.JSON,
    arrayFormat = 'comma',
    contentType = ContentType.JSON,
    formData,
    locale,
    method,
    params,
    responseType = undefined,
    uri,
}: Request): any => {
    const paramsSnakeCase = params
        ? toSnakeCase(withValues(params))
        : undefined;

    const safeParams = paramsSnakeCase
        ? queryString.stringify(paramsSnakeCase, {
              arrayFormat,
          })
        : undefined;

    let body: FormData | string | undefined;

    if (contentType === ContentType.JSON) {
        body = cleanFormData(formData);
    } else {
        body = encodeFormData(formData);
    }

    const q = uri.includes('?') ? '&' : '?';
    const search = safeParams ? `${q}${safeParams}` : '';

    const resolvedUrl = new URL(uri, window.location.href);
    const isSameOrigin =
        resolvedUrl.protocol === window.location.protocol &&
        resolvedUrl.host === window.location.host;

    const headers = {
        Accept: accept,
        'Active-Language': locale === 'ja' ? locale : undefined,
        // Let Content-Type be automatically determined when body instanceof FormData
        'Content-Type': body instanceof FormData ? undefined : contentType,
        'X-CSRFToken': isSameOrigin ? Cookies.get('csrftoken') : undefined,
        'X-MakeLeaps-Request-Source': 'UI',
        'X-MakeLeaps-Request-Tool': 'react',
    };

    return axios({
        data: body,
        headers,
        method,
        responseType,
        url: `${uri}${search}`,
    })
        .then(ResponseValidator)
        .then((response: ApiError<any> | ApiSuccess<any> | undefined) => {
            if (response instanceof ApiError) {
                throw response;
            }

            return response;
        })
        .catch((error: AxiosError) => {
            throw ErrorValidator(error);
        });
};

export const setUrl = (href: string) => {
    if (import.meta.env.DEV === 'true') return;
    if (import.meta.env.VITEST === 'true') return;
    if (import.meta.env.STORYBOOK === 'true') return;

    window.location.href = href;
};
